import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    CircularProgress,
    IconButton,
    FormControl,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TIMEZONE_OPTIONS } from '../constants/enums';
import { useTranslation } from 'react-i18next';
import { LanguageSelectField } from '../components/LanguageSelector';

function SettingRow({ label, description, children }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                py: 2.5,
                gap: 2,
            }}
        >
            <Box sx={{ minWidth: { sm: 220 }, flex: 1, pr: { sm: 3 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {description && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.875rem' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 280 }, minWidth: { sm: 240 } }}>
                {children}
            </Box>
        </Box>
    );
}

function Settings() {
    const { t } = useTranslation();
    const { api, currentUser, refreshUser, currentLanguage, changeLanguage } = useAuth();
    const navigate = useNavigate();

    const [currency, setCurrency] = useState(currentUser?.preferred_currency || 'CAD');
    const [timezone, setTimezone] = useState(currentUser?.preferred_timezone || 'America/New_York');
    const [language, setLanguage] = useState(currentLanguage || 'en');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (currentUser?.preferred_currency) {
            setCurrency(currentUser.preferred_currency);
        }
        if (currentUser?.preferred_timezone) {
            setTimezone(currentUser.preferred_timezone);
        }
    }, [currentUser]);

    useEffect(() => {
        if (currentLanguage) {
            setLanguage(currentLanguage);
        }
    }, [currentLanguage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        setError('');
        setSuccess('');

        try {
            // Update local language preference
            if (language !== currentLanguage) {
                changeLanguage(language);
            }

            await api.put('/users/me', {
                preferred_currency: currency,
                preferred_language: language,
                preferred_timezone: timezone
            });
            await refreshUser();
            setSuccess(t('Settings updated successfully'));
        } catch (err) {
            setError(err.response?.data?.detail || t('Failed to update settings'));
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Box sx={{ mt: 4, mb: 8, maxWidth: 800, mx: 'auto', px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }} color="primary" aria-label={t('Back')}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '-0.02em' }}>
                        {t('Settings')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {t('Manage your application preferences and regional configurations.')}
                    </Typography>
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 4 },
                    borderRadius: '24px',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    {t('General Settings')}
                </Typography>

                {error && <Alert severity="error" sx={{ my: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ my: 2, borderRadius: 2 }}>{success}</Alert>}

                <Box component="form" onSubmit={handleSubmit}>
                    <SettingRow
                        label={t('Preferred Language')}
                        description={t('Select your preferred display language.')}
                    >
                        <LanguageSelectField
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            disabled={submitLoading}
                            hideLabel
                            currency={currency}
                        />
                    </SettingRow>

                    <Divider sx={{ borderColor: 'divider' }} />

                    <SettingRow
                        label={t('Preferred Currency')}
                        description={t('Default currency used for balances, budgets, and reporting.')}
                    >
                        <FormControl fullWidth>
                            <Select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                disabled={submitLoading}
                                inputProps={{ 'aria-label': t('Preferred Currency') }}
                            >
                                <MenuItem value="CAD">{t('CAD - Canadian Dollar')}</MenuItem>
                                <MenuItem value="USD">{t('USD - US Dollar')}</MenuItem>
                                <MenuItem value="CNY">{t('CNY - Chinese Yuan')}</MenuItem>
                            </Select>
                        </FormControl>
                    </SettingRow>

                    <Divider sx={{ borderColor: 'divider' }} />

                    <SettingRow
                        label={t('Preferred Time Zone')}
                        description={t('Your local time zone for transaction timestamps and scheduling.')}
                    >
                        <FormControl fullWidth>
                            <Select
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                disabled={submitLoading}
                                inputProps={{ 'aria-label': t('Preferred Time Zone') }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                            >
                                {TIMEZONE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {t(opt.label)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </SettingRow>

                    <Divider sx={{ borderColor: 'divider', mb: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitLoading}
                            startIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ py: 1.2, px: 3, fontWeight: 600, borderRadius: 2 }}
                        >
                            {submitLoading ? t('Loading...') : t('Save Settings')}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

export default Settings;
